import React, { useState, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Collapse,
  Chip,
  FormControl,
  Select,
  MenuItem,
  GridLegacy as Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Tooltip,
  InputAdornment,
  SelectChangeEvent,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  AttachMoney as MoneyIcon,
  TrendingUp,
  People,
  Assessment,
  Refresh,
  FileDownload,
  Search,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  EmojiEvents,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { format, parseISO } from 'date-fns';

// Types
interface Sale {
  date: string;
  email: string;
  isWeekend: boolean;
  product?: string;
  Date?: string;
  Email?: string;
  Product?: string;
}

interface AgentCommissionData {
  agent: string;
  isCaliCrusher?: boolean;
  totalSales: number;
  weekdaySales: number;
  weekendSales: number;
  salesOver20: number;
  totalCommission: number;
  spiffTotal?: number;
  spiffCount?: number;
  validatedSales?: Sale[];
  nonValidatedSales?: Sale[];
  attributionOnlySales?: Sale[];
  cancelledSales?: Sale[];
  nonValidatedCount: number;
  attributionOnlyCount: number;
  cancelledCount: number;
}

interface CalculatedAgentData extends AgentCommissionData {
  calculatedTotalSales: number;
  calculatedWeekdaySales: number;
  calculatedWeekendSales: number;
  calculatedSalesOver20: number;
  calculatedTotalCommission: number;
  hasAdjustments: boolean;
}

interface CommissionData {
  commissions: AgentCommissionData[];
  weekStart: string;
  weekEnd: string;
}

type CommissionPeriod = 'currentWeek' | 'previousDay' | 'previousWeek' | 'customRange';
type OrderDirection = 'asc' | 'desc';
type OrderByField = 'agent' | 'totalSales' | 'weekdaySales' | 'weekendSales' | 'salesOver20' | 'totalCommission';

// Expandable Row Component
interface CommissionRowProps {
  agent: CalculatedAgentData;
  includedSales: Map<string, boolean>;
  onToggleSale: (key: string, checked: boolean) => void;
}

const CommissionRow: React.FC<CommissionRowProps> = ({ agent, includedSales, onToggleSale }) => {
  const [open, setOpen] = useState<boolean>(false);

  // Calculate dynamic metrics based on inclusions
  const calculateDynamicMetrics = () => {
    const validatedCount = (agent.validatedSales || []).length;
    
    let additionalSales = 0;
    (agent.nonValidatedSales || []).forEach(sale => {
      const key = `nv-${agent.agent}-${sale.date}-${sale.email}`;
      if (includedSales.get(key)) {
        additionalSales++;
      }
    });
    (agent.attributionOnlySales || []).forEach(sale => {
      const key = `ao-${agent.agent}-${sale.date}-${sale.email}`;
      if (includedSales.get(key)) {
        additionalSales++;
      }
    });
    
    if (additionalSales === 0) {
      return {
        totalSales: agent.totalSales,
        weekdaySales: agent.weekdaySales,
        weekendSales: agent.weekendSales,
        salesOver20: agent.salesOver20,
        totalCommission: agent.totalCommission,
        hasAdjustments: false,
        needsRecalc: false,
      };
    }
    
    return {
      totalSales: agent.totalSales + additionalSales,
      weekdaySales: agent.weekdaySales,
      weekendSales: agent.weekendSales,
      salesOver20: agent.salesOver20,
      totalCommission: agent.totalCommission,
      hasAdjustments: true,
      needsRecalc: true,
    };
  };

  const dynamicMetrics = calculateDynamicMetrics();
  const hasAdjustments = dynamicMetrics.hasAdjustments;

  return (
    <>
      <TableRow 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          backgroundColor: hasAdjustments ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
          '&:hover': { backgroundColor: hasAdjustments ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)' }
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" fontWeight={600}>
              {agent.agent}
            </Typography>
            {(agent.nonValidatedCount > 0 || agent.attributionOnlyCount > 0) && (
              <Tooltip title={`${agent.nonValidatedCount} non-validated, ${agent.attributionOnlyCount} attribution-only`}>
                <Warning color="warning" fontSize="small" />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip 
            label={dynamicMetrics.totalSales} 
            color={hasAdjustments ? "warning" : "default"}
            size="small"
          />
        </TableCell>
        <TableCell align="center">{dynamicMetrics.weekdaySales}</TableCell>
        <TableCell align="center">{dynamicMetrics.weekendSales}</TableCell>
        <TableCell align="center">
          {dynamicMetrics.salesOver20 > 0 ? (
            <Chip label={dynamicMetrics.salesOver20} color="success" size="small" />
          ) : (
            <Typography variant="body2" color="text.secondary">0</Typography>
          )}
        </TableCell>
        <TableCell align="right">
          {agent.spiffTotal && agent.spiffTotal > 0 ? (
            <Tooltip title={`${agent.spiffCount} bonus${agent.spiffCount !== 1 ? 'es' : ''}`}>
              <Chip
                label={`$${agent.spiffTotal.toLocaleString()}`}
                size="small"
                sx={{
                  backgroundColor: '#FFD700',
                  color: '#000',
                  fontWeight: 600
                }}
              />
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="h6" fontWeight={600} color="success.main">
            ${agent.calculatedTotalCommission.toLocaleString()}
          </Typography>
          {dynamicMetrics.needsRecalc && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
              +{dynamicMetrics.totalSales - agent.totalSales} pending
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Grid container spacing={3}>
                {/* Validated Sales */}
                {agent.validatedSales && agent.validatedSales.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="success" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Validated Sales ({agent.validatedSales.length})
                        </Typography>
                      </Box>
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {agent.validatedSales.map((sale, idx) => (
                          <Box 
                            key={idx} 
                            sx={{ 
                              p: 1, 
                              mb: 0.5, 
                              backgroundColor: 'white',
                              borderRadius: 1,
                              fontSize: '0.85rem'
                            }}
                          >
                            <Typography variant="caption" display="block">
                              <strong>{format(parseISO(sale.date), 'MMM dd, yyyy')}</strong>
                              {sale.isWeekend && <Chip label="Weekend" size="small" color="info" sx={{ ml: 1, height: 16 }} />}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {sale.email}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* Non-Validated Sales */}
                {agent.nonValidatedSales && agent.nonValidatedSales.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.08)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Warning color="warning" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Non-Validated Sales ({agent.nonValidatedSales.length})
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        In sales tracker but not attribution
                      </Typography>
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {agent.nonValidatedSales.map((sale, idx) => {
                          const key = `nv-${agent.agent}-${sale.date}-${sale.email}`;
                          return (
                            <Box 
                              key={idx} 
                              sx={{ 
                                p: 1, 
                                mb: 0.5, 
                                backgroundColor: 'white',
                                borderRadius: 1,
                                fontSize: '0.85rem'
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={includedSales.get(key) || false}
                                    onChange={(e) => onToggleSale(key, e.target.checked)}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      <strong>{format(parseISO(sale.date), 'MMM dd, yyyy')}</strong>
                                      {sale.isWeekend && <Chip label="Weekend" size="small" color="info" sx={{ ml: 1, height: 16 }} />}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {sale.email}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* Attribution-Only Sales */}
                {agent.attributionOnlySales && agent.attributionOnlySales.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.08)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ErrorIcon color="error" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Attribution-Only Sales ({agent.attributionOnlySales.length})
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        In attribution but not sales tracker
                      </Typography>
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {agent.attributionOnlySales.map((sale, idx) => {
                          const key = `ao-${agent.agent}-${sale.date}-${sale.email}`;
                          return (
                            <Box 
                              key={idx} 
                              sx={{ 
                                p: 1, 
                                mb: 0.5, 
                                backgroundColor: 'white',
                                borderRadius: 1,
                                fontSize: '0.85rem'
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={includedSales.get(key) || false}
                                    onChange={(e) => onToggleSale(key, e.target.checked)}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      <strong>{format(parseISO(sale.date), 'MMM dd, yyyy')}</strong>
                                      {sale.isWeekend && <Chip label="Weekend" size="small" color="info" sx={{ ml: 1, height: 16 }} />}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {sale.email}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* Cancelled Sales */}
                {agent.cancelledSales && agent.cancelledSales.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, backgroundColor: 'rgba(211, 47, 47, 0.12)', border: '2px solid rgba(211, 47, 47, 0.5)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ErrorIcon sx={{ mr: 1, color: '#d32f2f' }} />
                        <Typography variant="subtitle2" fontWeight={600} color="#d32f2f">
                          Cancelled Sales ({agent.cancelledSales.length})
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ mb: 1, display: 'block', color: '#d32f2f', fontWeight: 600 }}>
                        ⚠️ EXCLUDED from commission
                      </Typography>
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {agent.cancelledSales.map((sale, idx) => (
                          <Box 
                            key={idx} 
                            sx={{ 
                              p: 1, 
                              mb: 0.5, 
                              backgroundColor: 'white',
                              borderRadius: 1,
                              fontSize: '0.85rem',
                              opacity: 0.7
                            }}
                          >
                            <Typography variant="caption" display="block">
                              <strong>{format(parseISO(sale.date || sale.Date || ''), 'MMM dd, yyyy')}</strong>
                              <Chip label="CANCELLED" size="small" sx={{ ml: 1, height: 16, backgroundColor: '#d32f2f', color: 'white' }} />
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {sale.email || sale.Email}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CommissionCalculator: React.FC = () => {
  const [period, setPeriod] = useState<CommissionPeriod>('currentWeek');
  const [includedSales, setIncludedSales] = useState<Map<string, boolean>>(new Map());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [orderBy, setOrderBy] = useState<OrderByField>('totalCommission');
  const [order, setOrder] = useState<OrderDirection>('desc');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['commissions', period, startDate, endDate],
    queryFn: () => api.getCommissionData(period, period === 'customRange' ? startDate : null, period === 'customRange' ? endDate : null),
    refetchInterval: 5 * 60 * 1000,
    enabled: period !== 'customRange' || (period === 'customRange' && !!startDate && !!endDate),
    placeholderData: (previousData) => previousData,
  });

  
  const needsDateSelection = period === 'customRange' && (!startDate || !endDate);

  const handleToggleSale = (key: string, checked: boolean) => {
    setIncludedSales(prev => {
      const newMap = new Map(prev);
      if (checked) {
        newMap.set(key, true);
      } else {
        newMap.delete(key);
      }
      return newMap;
    });
  };

  const calculatedData = useMemo((): CalculatedAgentData[] | null => {
    if (!data?.commissions) return null;

    return data.commissions.map((agent: AgentCommissionData) => {
      let hasInclusions = false;
      const additionalSales: Sale[] = [];
      
      (agent.nonValidatedSales || []).forEach((sale: Sale) => {
        const key = `nv-${agent.agent}-${sale.date}-${sale.email}`;
        if (includedSales.get(key)) {
          hasInclusions = true;
          additionalSales.push(sale);
        }
      });
      
      (agent.attributionOnlySales || []).forEach((sale: Sale) => {
        const key = `ao-${agent.agent}-${sale.date}-${sale.email}`;
        if (includedSales.get(key)) {
          hasInclusions = true;
          additionalSales.push(sale);
        }
      });
      
      if (!hasInclusions) {
        return {
          ...agent,
          calculatedTotalSales: agent.totalSales,
          calculatedWeekdaySales: agent.weekdaySales,
          calculatedWeekendSales: agent.weekendSales,
          calculatedSalesOver20: agent.salesOver20,
          calculatedTotalCommission: agent.totalCommission + (agent.spiffTotal || 0),
          hasAdjustments: false
        };
      }
      
      let allSales = [...agent.validatedSales || [], ...additionalSales];
      allSales.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      
      const agentLower = agent.agent.toLowerCase();
      const isMikeHales = agentLower.includes('mike h') || agentLower.includes('michael hales');
      const isJosephM = agentLower.includes('joseph m') || agentLower.includes('josephm');
      const isMattJames = agentLower.includes('matt j') || agentLower.includes('matthew james');
      // Cali Crushers team → flat $50 per sale (Joseph keeps his existing rate)
      const isCaliCrusher = !!agent.isCaliCrusher && !agentLower.includes('joseph');
      
      let totalCommission = 0;
      let weekdaySales = 0;
      let weekendSales = 0;
      let salesOver20 = 0;
      
      allSales.forEach((sale, index) => {
        const saleNumber = index + 1;
        let saleCommission = 0;
        
        if (isCaliCrusher) {
          // Cali Crushers team: Flat $50 per sale (no weekend bonus, no over-20 accelerator)
          saleCommission = 50;
          if (sale.isWeekend) {
            weekendSales++;
          } else {
            weekdaySales++;
          }
        } else if (isMikeHales) {
          // Mike H: Flat $75 per sale (no weekday/weekend tracking)
          saleCommission = 75;
        } else if (isJosephM || isMattJames) {
          // Joseph M & Matt James: Flat $65 per sale (no weekday/weekend/over20 tracking)
          saleCommission = 65;
        } else {
          if (saleNumber > 20) {
            saleCommission = 65;
            salesOver20++;
          } else {
            saleCommission = 50;
          }
          
          if (sale.isWeekend) {
            saleCommission += 10;
            weekendSales++;
          } else {
            weekdaySales++;
          }
        }
        
        totalCommission += saleCommission;
      });
      
      return {
        ...agent,
        calculatedTotalSales: allSales.length,
        calculatedWeekdaySales: weekdaySales,
        calculatedWeekendSales: weekendSales,
        calculatedSalesOver20: salesOver20,
        calculatedTotalCommission: totalCommission + (agent.spiffTotal || 0),
        hasAdjustments: true
      };
    });
  }, [data, includedSales]);

  const filteredAndSortedData = useMemo(() => {
    if (!calculatedData) return [];
    
    let filtered = calculatedData;
    
    if (searchQuery) {
      filtered = filtered.filter(agent =>
        agent.agent.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    filtered = [...filtered].sort((a, b) => {
      const aValue = orderBy === 'agent' ? a[orderBy] : (a as any)[`calculated${orderBy.charAt(0).toUpperCase() + orderBy.slice(1)}`] || a[orderBy];
      const bValue = orderBy === 'agent' ? b[orderBy] : (b as any)[`calculated${orderBy.charAt(0).toUpperCase() + orderBy.slice(1)}`] || b[orderBy];
      
      if (orderBy === 'agent') {
        return order === 'asc' 
          ? (aValue as string).localeCompare(bValue as string)
          : (bValue as string).localeCompare(aValue as string);
      }
      
      return order === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
    });
    
    return filtered;
  }, [calculatedData, searchQuery, orderBy, order]);

  const summaryStats = useMemo(() => {
    if (!calculatedData) return null;
    
    const totalAgents = calculatedData.length;
    const totalCommission = calculatedData.reduce((sum, agent) => sum + agent.calculatedTotalCommission, 0);
    const totalSales = calculatedData.reduce((sum, agent) => sum + agent.calculatedTotalSales, 0);
    const avgCommission = totalAgents > 0 ? totalCommission / totalAgents : 0;
    
    return {
      totalAgents,
      totalCommission,
      totalSales,
      avgCommission
    };
  }, [calculatedData]);

  const handleSort = (property: OrderByField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleExport = () => {
    if (!calculatedData) return;
    
    const headers = ['Agent', 'Total Sales', 'Weekday Sales', 'Weekend Sales', 'Sales Over 20', 'SPIFFs', 'Total Commission'];
    const rows = calculatedData.map(agent => [
      agent.agent,
      agent.calculatedTotalSales,
      agent.calculatedWeekdaySales,
      agent.calculatedWeekendSales,
      agent.calculatedSalesOver20,
      agent.spiffTotal || 0,
      agent.calculatedTotalCommission
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission-${period}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleWeeklyInvoicingExport = () => {
    if (!calculatedData) return;

    setIsExporting(true);

    try {
      // Collect all sales to include in the invoicing export
      const allSalesRecords: { date: string; customerName: string; product: string; isWeekend: boolean }[] = [];

      calculatedData.forEach(agent => {
        // Always include validated sales
        (agent.validatedSales || []).forEach(sale => {
          allSalesRecords.push({
            date: sale.date,
            customerName: sale.email,
            product: sale.product || sale.Product || '',
            isWeekend: sale.isWeekend,
          });
        });

        // Always include attribution-only sales
        (agent.attributionOnlySales || []).forEach(sale => {
          allSalesRecords.push({
            date: sale.date,
            customerName: sale.email,
            product: sale.product || sale.Product || '',
            isWeekend: sale.isWeekend,
          });
        });

        // Include manually checked non-validated sales
        (agent.nonValidatedSales || []).forEach(sale => {
          const key = `nv-${agent.agent}-${sale.date}-${sale.email}`;
          if (includedSales.get(key)) {
            allSalesRecords.push({
              date: sale.date,
              customerName: sale.email,
              product: sale.product || sale.Product || '',
              isWeekend: sale.isWeekend,
            });
          }
        });
      });

      // Sort by date ascending
      allSalesRecords.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Build CSV: Date, Customer Name, Product, Weekend
      const headers = ['Date', 'Customer Name', 'Product', 'Weekend'];
      const csvRows = allSalesRecords.map(record => {
        const formattedDate = format(parseISO(record.date), 'MM/dd/yyyy');
        // Sanitize: strip newlines/carriage returns, escape double-quotes
        const customerName = (record.customerName || '').replace(/[\r\n]+/g, ' ').replace(/"/g, '""');
        const product = (record.product || '').replace(/[\r\n]+/g, ' ').replace(/"/g, '""');
        return `${formattedDate},"${customerName}","${product}",${record.isWeekend ? 'Yes' : 'No'}`;
      });

      // Prepend UTF-8 BOM so Excel opens the CSV with correct encoding
      const BOM = '\uFEFF';
      const csvContent = BOM + [headers.join(','), ...csvRows].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const dateRangeLabel = period === 'customRange' && startDate && endDate
        ? `${startDate}_to_${endDate}`
        : period;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Weekly_Invoicing_${dateRangeLabel}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating weekly invoicing export:', error);
      alert('Failed to generate weekly invoicing export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Error loading commission data</Typography>
          <Typography variant="body2">{(error as Error).message}</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon fontSize="large" color="primary" />
              Commission Calculator
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data?.weekStart && data?.weekEnd && (
                <>Week of {format(parseISO(data.weekStart), 'MMM dd')} - {format(parseISO(data.weekEnd), 'MMM dd, yyyy')}</>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={period}
                onChange={(e: SelectChangeEvent<CommissionPeriod>) => setPeriod(e.target.value as CommissionPeriod)}
              >
                <MenuItem value="currentWeek">Current Week</MenuItem>
                <MenuItem value="previousDay">Previous Day</MenuItem>
                <MenuItem value="previousWeek">Previous Week</MenuItem>
                <MenuItem value="customRange">Custom Range</MenuItem>
              </Select>
            </FormControl>
            
            {period === 'customRange' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  From:
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="Start date"
                  sx={{ minWidth: { xs: '130px', sm: 150 } }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: endDate || undefined,
                  }}
                />
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  To:
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="End date"
                  sx={{ minWidth: { xs: '130px', sm: 150 } }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: startDate || undefined,
                  }}
                />
              </Box>
            )}
            
            <Tooltip title="Refresh data">
              <IconButton onClick={() => refetch()} color="primary">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {needsDateSelection && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Please select both start and end dates to view commission data for the custom range.
          </Typography>
        </Alert>
      )}

      {summaryStats && !needsDateSelection && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="caption" display="block">
                      Total Agents
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {summaryStats.totalAgents}
                    </Typography>
                  </Box>
                  <People sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="caption" display="block">
                      Total Payout
                    </Typography>
                    <Typography variant="h4" fontWeight={600} color="success.main">
                      ${summaryStats.totalCommission.toLocaleString()}
                    </Typography>
                  </Box>
                  <MoneyIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="caption" display="block">
                      Total Sales
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {summaryStats.totalSales}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 48, color: 'info.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="caption" display="block">
                      Avg Commission
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      ${Math.round(summaryStats.avgCommission).toLocaleString()}
                    </Typography>
                  </Box>
                  <Assessment sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="success"
              startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownload />}
              onClick={handleWeeklyInvoicingExport}
              disabled={isExporting || !data}
            >
              {isExporting ? 'Exporting...' : 'Weekly Invoicing Export'}
            </Button>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExport}
              disabled={!calculatedData || calculatedData.length === 0}
            >
              Export to CSV
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
              <TableCell width={50} />
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'agent'}
                  direction={orderBy === 'agent' ? order : 'asc'}
                  onClick={() => handleSort('agent')}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Agent</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'totalSales'}
                  direction={orderBy === 'totalSales' ? order : 'asc'}
                  onClick={() => handleSort('totalSales')}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Total Sales</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'weekdaySales'}
                  direction={orderBy === 'weekdaySales' ? order : 'asc'}
                  onClick={() => handleSort('weekdaySales')}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Weekday</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'weekendSales'}
                  direction={orderBy === 'weekendSales' ? order : 'asc'}
                  onClick={() => handleSort('weekendSales')}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Weekend</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'salesOver20'}
                  direction={orderBy === 'salesOver20' ? order : 'asc'}
                  onClick={() => handleSort('salesOver20')}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Over 20</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Performance bonuses and incentives">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    <EmojiEvents sx={{ fontSize: 16, color: '#FFD700' }} />
                    <Typography variant="subtitle2" fontWeight={600}>SPIFFs</Typography>
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'totalCommission'}
                  direction={orderBy === 'totalCommission' ? order : 'asc'}
                  onClick={() => handleSort('totalCommission')}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Total Commission</Typography>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchQuery ? 'No agents found matching your search' : 'No commission data available for this period'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedData.map((agent) => (
                <CommissionRow
                  key={agent.agent}
                  agent={agent}
                  includedSales={includedSales}
                  onToggleSale={handleToggleSale}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {includedSales.size > 0 && (
        <Paper sx={{ p: 2, mt: 2, backgroundColor: 'rgba(25, 118, 210, 0.08)' }}>
          <Typography variant="body2" color="primary" fontWeight={600}>
            ℹ️ You have {includedSales.size} manual inclusion{includedSales.size !== 1 ? 's' : ''} selected. Commissions are recalculated dynamically.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Note: These selections are not saved. To persist, you'll need to implement database storage.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default CommissionCalculator;
